import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { initAnalytics, trackPageView, trackEvent, trackAction, startHeartbeat, stopHeartbeat } from '../../services/analytics';

function resolveMerchantId(): number | undefined {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u.merchant_id) return u.merchant_id;
    }
    const shopsStr = localStorage.getItem('stores');
    const shop = localStorage.getItem('shop');
    if (shopsStr && shop) {
      const stores = JSON.parse(shopsStr);
      const store = stores.find((s: any) => s.shop === shop || s.name === shop);
      if (store?.id) return store.id;
    }
    if (userStr) {
      const u = JSON.parse(userStr);
      return u.id;
    }
  } catch { /* ignore */ }
  return undefined;
}

export function AnalyticsProvider() {
  const location = useLocation();
  const pageEnterRef = useRef<number>(Date.now());
  const prevPathRef = useRef<string>('');

  useEffect(() => {
    initAnalytics();
    startHeartbeat();

    return () => stopHeartbeat();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const trackSource = params.get('utm_source') || params.get('track_source');
    const trackCampaign = params.get('utm_campaign') || params.get('track_campaign');
    const trackMedium = params.get('utm_medium') || params.get('track_medium');
    const notificationId = params.get('ntf_id');

    if (notificationId || trackSource || trackCampaign) {
      trackEvent('Notification Link Clicked', {
        notification_id: notificationId || undefined,
        source: trackSource || undefined,
        campaign: trackCampaign || undefined,
        medium: trackMedium || undefined,
        url: location.pathname + location.search,
        merchant_id: resolveMerchantId(),
      });
    }
  }, []);

  useEffect(() => {
    const now = Date.now();
    const prevPath = prevPathRef.current;
    const duration = prevPath ? Math.round((now - pageEnterRef.current) / 1000) : 0;

    trackPageView(location.pathname + location.search, {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
      merchant_id: resolveMerchantId(),
      ...(prevPath ? { previous_path: prevPath, duration_prev_page_sec: duration } : {}),
    });

    prevPathRef.current = location.pathname + location.search;
    pageEnterRef.current = now;
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const duration = Math.round((Date.now() - pageEnterRef.current) / 1000);
      trackEvent('Page Exit', {
        path: location.pathname + location.search,
        duration_sec: duration,
        merchant_id: resolveMerchantId(),
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleStoreSwitched = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      trackEvent('Store Switched', { store: detail?.store });
    };

    const handleStoreConnected = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      trackEvent('Store Connected', { shop: detail?.shop });
    };

    window.addEventListener('store_switched', handleStoreSwitched);
    window.addEventListener('storeConnected', handleStoreConnected);

    return () => {
      window.removeEventListener('store_switched', handleStoreSwitched);
      window.removeEventListener('storeConnected', handleStoreConnected);
    };
  }, []);

  useEffect(() => {
    const isInsideNav = (el: HTMLElement | null): boolean => {
      while (el) {
        const tag = el.tagName.toLowerCase();
        if (tag === 'nav' || tag === 'header') return true;
        const cls = el.className;
        if (typeof cls === 'string' && /sidebar|navbar|nav-|menu|header/i.test(cls)) return true;
        el = el.parentElement;
      }
      return false;
    };

    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>(
        'button, [role="tab"], [role="button"], .btn, [data-track-action]'
      );
      if (!target) return;
      if (isInsideNav(target)) return;

      const action = target.getAttribute('data-track-action')
        || target.getAttribute('aria-label')
        || target.textContent?.trim()
        || 'click';

      const label = target.getAttribute('data-track-label') || undefined;

      trackAction(location.pathname, action.slice(0, 80), label);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [location.pathname]);

  return null;
}
